import { createContext, useContext } from 'react'

import { WorkflowContainerFragment, WorkflowManifestFragment } from 'generated/resources/graphql'
import { z } from 'zod'

import { EdgeUI, NodeUI } from 'modules/workflows/components/Node/types'
import {
  VariablesConfig,
  type EntityResolution,
} from 'modules/workflows/components/Sidepanel/VariableSelectorTypes'
import {
  FormContext,
  NodeSidepanelSchema,
} from 'modules/workflows/components/Sidepanel/schemas/nodeSidepanelSchema'
import { BuildingBlock } from 'modules/workflows/components/WorkflowBuildingBlocksBox'

export type TriggerConfig = { title: string; description: string }

export type { EntityResolution }

export type HandlerFormComponentPayload = { [key: string]: unknown }
export type HandlerFormComponent = {
  payload: HandlerFormComponentPayload
  onChange: (values: HandlerFormComponentPayload, isValid: boolean) => void
}

export type HandlersConfig = {
  [handlerName: string]: {
    dropDownLabel: string
    component: React.ComponentType<HandlerFormComponent>
    initializer: (payload: HandlerFormComponentPayload) => { [key: string]: unknown }
    beforeSave?: (
      payload: HandlerFormComponentPayload,
      handlerType: string,
      context?: {
        nodeId: string
        workflow: WorkflowContainerFragment
        nodesUI?: NodeUI[]
      }
    ) => { [key: string]: unknown }
    schema: (formContext: FormContext) => z.ZodSchema<NodeSidepanelSchema>
  }
}

export type MenuControls = {
  closeAllMenus: () => void
  registerMenu: (id: string, closeCallback: () => void) => () => void
}

type WorkflowContextProps = {
  buildingBlocks: BuildingBlock[]
  nodesUI: NodeUI[]
  setNodesUI: React.Dispatch<React.SetStateAction<NodeUI[]>>
  edgesUI: EdgeUI[]
  setEdgesUI: React.Dispatch<React.SetStateAction<EdgeUI[]>>
  isDirty: boolean
  elevateNodeZIndex: (nodeId: string) => void
  restoreNodeZIndex: (nodeId: string) => void
  triggerConfig: TriggerConfig
  workflowManifest: WorkflowManifestFragment
  workflow: WorkflowContainerFragment
  handlersConfig: HandlersConfig
  menuControls: MenuControls
  sidepanelDirty: boolean
  setSidepanelDirty: (dirty: boolean) => void
  variablesConfig: VariablesConfig
  entityResolution: EntityResolution
  /**
   * Multi Action "edit" mode: we're editing an existing parallel task.
   * When set, the sidepanel shows TaskNodeSidepanel/ApprovalNodeSidepanel for that task
   * (built from parallelTasks). On save we update the task in the array; on back we clear this.
   */
  multiActionEditingTaskId: string | null
  setMultiActionEditingTaskId: (id: string | null) => void
  /**
   * Multi Action "draft" mode: we're creating a new parallel task (not in the list yet).
   * Set when user clicks "New action" (Task/Approval) or empty state on the canvas.
   * Sidepanel shows Task/Approval form with default values; on save we push to parallelTasks,
   * on cancel/back we clear this and return to the task list.
   */
  multiActionDraft: { nodeId: string; type: 'task' | 'approvals' } | null
  setMultiActionDraft: (draft: { nodeId: string; type: 'task' | 'approvals' } | null) => void
}

export const WorkflowContext = createContext<WorkflowContextProps>({
  buildingBlocks: [],
  nodesUI: [],
  edgesUI: [],
  isDirty: false,
  sidepanelDirty: false,
  setSidepanelDirty: () => {
    throw new Error('WorkflowContext not properly initialized')
  },
  elevateNodeZIndex: () => {
    throw new Error('WorkflowContext not properly initialized')
  },
  restoreNodeZIndex: () => {
    throw new Error('WorkflowContext not properly initialized')
  },
  triggerConfig: {
    title: 'Trigger',
    description: 'This is the starting point of your useCase',
  },
  workflowManifest: {
    useCase: 'generic',
    workflowDataInputs: [],
    dynamicWorkflowDataInputs: [],
    handlers: [],
  },
  workflow: {
    id: 0,
    companyId: 0,
    disabled: false,
    identifier: '',
    status: 'draft',
    useCase: 'generic',
    version: 1,
    workflowEdges: [],
    workflowNodes: [],
    workflowNodeFiles: [],
    workflowManifest: {
      useCase: 'generic',
      workflowDataInputs: [],
      dynamicWorkflowDataInputs: [],
      handlers: [],
    },
  },
  setNodesUI: () => {
    throw new Error('WorkflowContext not properly initialized')
  },
  setEdgesUI: () => {
    throw new Error('WorkflowContext not properly initialized')
  },
  handlersConfig: {},
  menuControls: {
    closeAllMenus: () => {
      throw new Error('WorkflowContext not properly initialized')
    },
    registerMenu: () => {
      throw new Error('WorkflowContext not properly initialized')
    },
  },
  variablesConfig: {},
  entityResolution: {},
  multiActionEditingTaskId: null,
  setMultiActionEditingTaskId: () => {
    throw new Error('WorkflowContext not properly initialized')
  },
  multiActionDraft: null,
  setMultiActionDraft: () => {
    throw new Error('WorkflowContext not properly initialized')
  },
})

export const useWorkflowContext = () => useContext(WorkflowContext)
